import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Icon } from "@/components/Icon";
import { useAuth } from "@/hooks/useAuth";
import { captureScreenshot } from "./captureScreenshot";
import { FeedbackModal } from "./FeedbackModal";

interface FeedbackButtonProps {
  /** Called after the click handler runs (e.g. close mobile drawer). */
  onActivate?: () => void;
}

/**
 * Sidebar entry that opens the feedback modal. Captures a screenshot of the
 * page BEFORE mounting the modal so the modal itself does not appear in the
 * shot. Falls back to a text-only submit if capture fails.
 */
export function FeedbackButton({ onActivate }: FeedbackButtonProps) {
  const { t } = useTranslation();
  const { isSignedIn } = useAuth();
  const [capturing, setCapturing] = useState(false);
  const [open, setOpen] = useState(false);
  const [screenshot, setScreenshot] = useState<Blob | null>(null);

  if (!isSignedIn) return null;

  async function handleClick() {
    if (capturing || open) return;
    onActivate?.();
    setCapturing(true);
    // Yield a frame so the click ripple / drawer-close paints before we
    // freeze the main thread for html2canvas.
    await new Promise((r) => requestAnimationFrame(() => r(null)));
    const blob = await captureScreenshot();
    setScreenshot(blob);
    setCapturing(false);
    setOpen(true);
  }

  function handleClose() {
    setOpen(false);
    setScreenshot(null);
  }

  return (
    <>
      <button
        type="button"
        className="nav-item"
        onClick={handleClick}
        disabled={capturing}
        aria-busy={capturing}
      >
        <span className="nav-icon">
          <Icon name="chat" size={16} />
        </span>
        {capturing ? t("feedback.capturing") : t("feedback.button")}
      </button>
      {open && <FeedbackModal screenshot={screenshot} onClose={handleClose} />}
    </>
  );
}
